import axios from "axios";
import { axiosRequest, type AxiosResult } from "@/lib/axios";

const API_BASE_URL = import.meta.env.VITE_SERVICE_CORE_URL;

export interface AudioUploadResponse {
	audio_url: string;
	audio_duration: number | null;
}

export interface AvatarUploadResponse {
	customer_avatar_url: string;
}

export interface CompanyLogoUploadResponse {
	customer_company_logo_url: string;
}

function getFileExtension(fileName: string): string {
	const ext = fileName.split(".").pop();
	return ext ? ext.toLowerCase() : "";
}

function normalizeAudioFile(file: File): File {
	const extension = getFileExtension(file.name);
	const normalizedMimeTypeByExtension: Record<string, string> = {
		mp3: "audio/mpeg",
		wav: "audio/wav",
		ogg: "audio/ogg",
		m4a: "audio/mp4",
	};

	const normalizedType = normalizedMimeTypeByExtension[extension];
	if (!normalizedType) {
		return file;
	}

	// Some browsers report M4A files as `video/mp4` or leave the type empty.
	// Rebuild the File so the multipart part carries a stable audio MIME type.
	if (file.type === normalizedType) {
		return file;
	}

	return new File([file], file.name, {
		type: normalizedType,
		lastModified: file.lastModified,
	});
}

async function uploadFileWithProgress<T>(url: string, file: File, fieldName: string, onProgress?: (percent: number) => void): Promise<T> {
	const formData = new FormData();
	formData.append(fieldName, file, file.name);

	try {
		const response = await axios.post(url, formData, {
			withCredentials: true,
			headers: {
				Accept: "application/json",
				"Content-Type": "multipart/form-data",
			},
			onUploadProgress: (event) => {
				if (event.total && onProgress) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress(percent);
				}
			},
		});

		return response.data?.data || response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message = error.response?.data?.message || error.message || "Upload failed.";
			throw new Error(message);
		}

		throw new Error("Upload failed. Please try again.");
	}
}

export const reviewUploadsApi = {
	/**
	 * Upload audio file for a review
	 * Supports progress tracking for larger files
	 */
	uploadAudio: (workspaceId: string, reviewId: string, file: File, onProgress?: (percent: number) => void): Promise<AudioUploadResponse> => {
		const url = `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/audio`;
		return uploadFileWithProgress<AudioUploadResponse>(url, normalizeAudioFile(file), "audio", onProgress);
	},

	/**
	 * Upload customer avatar image for a review
	 */
	uploadAvatar: (workspaceId: string, reviewId: string, file: File): Promise<AxiosResult<AvatarUploadResponse>> => {
		const formData = new FormData();
		formData.append("avatar", file);

		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/customer-avatar`,
			method: "POST",
			data: formData,
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	},

	/**
	 * Upload customer company logo for a review
	 */
	uploadCompanyLogo: (workspaceId: string, reviewId: string, file: File): Promise<AxiosResult<CompanyLogoUploadResponse>> => {
		const formData = new FormData();
		formData.append("logo", file);

		return axiosRequest({
			url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/customer-company-logo`,
			method: "POST",
			data: formData,
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	},
};
